import React, { useState } from 'react';
import {
  Card,
  Typography,
  Form,
  Input,
  Button,
  Switch,
  Divider,
  Space,
  message,
  Tabs,
  Row,
  Col,
  Select,
  InputNumber,
  Alert,
  Tooltip,
  Spin,
  Modal,
  List,
  Tag,
  Statistic,
} from 'antd';
import {
  SaveOutlined,
  ReloadOutlined,
  DownloadOutlined,
  DatabaseOutlined,
  CheckCircleOutlined,
  CloudUploadOutlined,
  ClockCircleOutlined,
  SettingOutlined,
  CodeOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import SystemHealth from './SystemHealth';

const { TextArea } = Input;
const { Option } = Select;

const Settings = () => {
  const location = useLocation();
  // Which group to show is driven by the sidebar route (/settings vs /developer)
  const topTab = location.pathname.startsWith('/developer') ? 'developer' : 'settings';
  const [form] = Form.useForm();
  const [securityForm] = Form.useForm();
  const [emailForm] = Form.useForm();
  const [notificationForm] = Form.useForm();
  const [systemForm] = Form.useForm();
  const [billingForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  // Fetch system settings
  const { isLoading } = useQuery(
    'system-settings',
    async () => {
      const response = await api.get('/admin/settings');
      return response.data?.data || response.data || {};
    },
    {
      onSuccess: (data) => {
        // Set form values
        form.setFieldsValue(data.general || {});
        securityForm.setFieldsValue(data.security || {});
        emailForm.setFieldsValue(data.email || {});
        notificationForm.setFieldsValue(data.notifications || {});
        systemForm.setFieldsValue(data.system || {});
      },
      onError: (_error) => {
        message.error('Failed to load system settings');
      },
    },
  );

  // Fetch billing settings
  const {
    isLoading: billingLoading,
    refetch: refetchBillingSettings,
  } = useQuery(
    'billing-settings',
    async () => {
      const response = await api.get('/admin/billing/settings');
      return response.data?.data || {};
    },
    {
      onSuccess: (data) => {
        billingForm.setFieldsValue({
          auto_generate_invoice: !!data.auto_generate_invoice,
        });
      },
      onError: (_error) => {
        message.error('Failed to load billing settings');
      },
    },
  );

  // Update billing settings mutation
  const updateBillingSettingsMutation = useMutation(
    async (values) => {
      const response = await api.put('/admin/billing/settings', values);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Billing settings updated successfully');
        refetchBillingSettings();
      },
      onError: (_error) => {
        message.error('Failed to update billing settings');
      },
    },
  );

  // Fetch backup status
  const {
    data: backupStatus,
    isLoading: backupLoading,
    refetch: refetchBackup,
  } = useQuery(
    'backup-status',
    async () => {
      const response = await api.get('/admin/settings/backup-status');
      return response.data?.data || response.data || {};
    },
    {
      onError: (error) => {
        console.error('Failed to load backup status:', error);
      },
      refetchInterval: 30000,
    },
  );

  // Update settings mutation
  const updateSettingsMutation = useMutation(
    async ({ category: _category, values }) => {
      const response = await api.put('/admin/settings', values);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Settings updated successfully');
        queryClient.invalidateQueries('system-settings');
        setLoading(false);
      },
      onError: (_error) => {
        message.error('Failed to update settings');
        setLoading(false);
      },
    },
  );

  // Backup system mutation
  const backupSystemMutation = useMutation(
    async (type) => {
      const response = await api.post('/admin/system/backup', { type });
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('System backup created successfully');
      },
      onError: (_error) => {
        message.error('Failed to create backup');
      },
    },
  );

  // Clear cache mutation
  const clearCacheMutation = useMutation(
    async () => {
      const response = await api.post('/admin/system/clear-cache');
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Cache cleared successfully');
        queryClient.invalidateQueries('system-status');
      },
      onError: (_error) => {
        message.error('Failed to clear cache');
      },
    },
  );

  // Google Drive backup mutation
  const runDriveBackupMutation = useMutation(
    async (upload) => {
      const response = await api.post('/admin/settings/run-backup', { upload });
      return response.data;
    },
    {
      onSuccess: (data) => {
        if (data.success) {
          message.success(data.message || 'Backup completed successfully');
          refetchBackup();
        } else {
          message.warning(data.message || 'Backup completed with warnings');
        }
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to run backup');
      },
    },
  );

  const handleSave = (category) => {
    setLoading(true);
    let formInstance;

    switch (category) {
      case 'general':
        formInstance = form;
        break;
      case 'security':
        formInstance = securityForm;
        break;
      case 'email':
        formInstance = emailForm;
        break;
      case 'notifications':
        formInstance = notificationForm;
        break;
      case 'system':
        formInstance = systemForm;
        break;
      default:
        formInstance = form;
    }

    formInstance
      .validateFields()
      .then((values) => {
        updateSettingsMutation.mutate({ category, values });
      })
      .catch((_error) => {
        message.error('Please fill in all required fields');
        setLoading(false);
      });
  };

  const handleReset = (category) => {
    let formInstance;

    switch (category) {
      case 'general':
        formInstance = form;
        break;
      case 'security':
        formInstance = securityForm;
        break;
      case 'email':
        formInstance = emailForm;
        break;
      case 'notifications':
        formInstance = notificationForm;
        break;
      case 'system':
        formInstance = systemForm;
        break;
      default:
        formInstance = form;
    }

    formInstance.resetFields();
    message.info('Settings reset to defaults');
  };

  const handleSaveBilling = () => {
    billingForm
      .validateFields()
      .then((values) => {
        updateBillingSettingsMutation.mutate({
          auto_generate_invoice: !!values.auto_generate_invoice,
        });
      })
      .catch((_error) => {
        message.error('Please check the billing settings');
      });
  };

  const handleBackup = (type) => {
    Modal.confirm({
      title: `Create ${type} backup?`,
      content: `This will create a ${type} backup of the entire system.`,
      okText: 'Backup',
      cancelText: 'Cancel',
      onOk: () => {
        backupSystemMutation.mutate(type);
      },
    });
  };

  const handleClearCache = () => {
    Modal.confirm({
      title: 'Clear System Cache?',
      content: 'This will clear all cached data and may temporarily slow down the system.',
      okText: 'Clear Cache',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        clearCacheMutation.mutate();
      },
    });
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {topTab === 'settings' && (
        <Card
          title={
            <div className="settings-head">
              <span className="settings-head-title">
                <SettingOutlined /> Admin Settings
              </span>
              <span className="settings-head-sub">
                Everyday configuration for running the platform
              </span>
            </div>
          }
        >
          <Tabs defaultActiveKey="general">
            {/* General Settings */}
            <Tabs.TabPane tab="General" key="general">
              <Form
                form={form}
                layout="vertical"
                initialValues={{
                  siteName: 'Kankali Mart Admin',
                  siteEmail: 'admin@kankalimart.com',
                  siteDescription: 'Comprehensive e-commerce and service management platform',
                  maintenanceMode: false,
                  timezone: 'Asia/Kathmandu',
                  dateFormat: 'YYYY-MM-DD',
                  timeFormat: '24h',
                  language: 'en',
                }}
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Site Name"
                      name="siteName"
                      rules={[{ required: true, message: 'Please input site name!' }]}
                    >
                      <Input placeholder="Enter site name" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Site Email"
                      name="siteEmail"
                      rules={[
                        { required: true, type: 'email', message: 'Please input valid email!' },
                      ]}
                    >
                      <Input placeholder="admin@example.com" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Timezone" name="timezone">
                      <Select placeholder="Select timezone">
                        <Option value="Asia/Kathmandu">Asia/Kathmandu</Option>
                        <Option value="UTC">UTC</Option>
                        <Option value="America/New_York">America/New_York</Option>
                        <Option value="Europe/London">Europe/London</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Language" name="language">
                      <Select placeholder="Select language">
                        <Option value="en">English</Option>
                        <Option value="ne">Nepali</Option>
                        <Option value="hi">Hindi</Option>
                        <Option value="zh">Chinese</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item label="Site Description" name="siteDescription">
                  <TextArea rows={3} placeholder="Enter site description" />
                </Form.Item>
                <Form.Item label="Maintenance Mode" name="maintenanceMode" valuePropName="checked">
                  <Switch checkedChildren="On" unCheckedChildren="Off" />
                </Form.Item>
                <Alert
                  message="Maintenance Mode"
                  description="When enabled, the site will show a maintenance page to all users except administrators."
                  type="warning"
                  showIcon
                  style={{ marginTop: 16 }}
                />
                <Space style={{ marginTop: 24 }}>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={() => handleSave('general')}
                    loading={loading}
                  >
                    Save General Settings
                  </Button>
                  <Button icon={<ReloadOutlined />} onClick={() => handleReset('general')}>
                    Reset to Defaults
                  </Button>
                </Space>
              </Form>
            </Tabs.TabPane>

            {/* Security Settings */}
            <Tabs.TabPane tab="Security" key="security">
              <Form
                form={securityForm}
                layout="vertical"
                initialValues={{
                  enable2FA: false,
                  sessionTimeout: 30,
                  maxLoginAttempts: 5,
                  lockoutDuration: 15,
                  passwordMinLength: 8,
                  passwordRequireUppercase: true,
                  passwordRequireNumbers: true,
                  passwordRequireSymbols: false,
                  enableCaptcha: false,
                  enableIpWhitelist: false,
                }}
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Session Timeout (minutes)" name="sessionTimeout">
                      <InputNumber min={5} max={480} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Maximum Login Attempts" name="maxLoginAttempts">
                      <InputNumber min={3} max={10} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Lockout Duration (minutes)" name="lockoutDuration">
                      <InputNumber min={5} max={1440} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Password Minimum Length" name="passwordMinLength">
                      <InputNumber min={6} max={32} />
                    </Form.Item>
                  </Col>
                </Row>
                <Divider>Password Requirements</Divider>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      label="Require Uppercase"
                      name="passwordRequireUppercase"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      label="Require Numbers"
                      name="passwordRequireNumbers"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      label="Require Symbols"
                      name="passwordRequireSymbols"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>
                <Divider>Additional Security</Divider>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item label="Enable 2FA" name="enable2FA" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="Enable Captcha" name="enableCaptcha" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      label="IP Whitelist"
                      name="enableIpWhitelist"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>
                <Space style={{ marginTop: 24 }}>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={() => handleSave('security')}
                    loading={loading}
                  >
                    Save Security Settings
                  </Button>
                  <Button icon={<ReloadOutlined />} onClick={() => handleReset('security')}>
                    Reset to Defaults
                  </Button>
                </Space>
              </Form>
            </Tabs.TabPane>

            {/* Email Settings */}
            <Tabs.TabPane tab="Email" key="email">
              <Form
                form={emailForm}
                layout="vertical"
                initialValues={{
                  driver: 'smtp',
                  host: 'smtp.gmail.com',
                  port: 587,
                  encryption: 'tls',
                  username: '',
                  password: '',
                  fromAddress: 'noreply@kankalimart.com',
                  fromName: 'Kankali Mart',
                  enableQueue: true,
                  enableRetry: true,
                }}
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Mail Driver" name="driver">
                      <Select placeholder="Select mail driver">
                        <Option value="smtp">SMTP</Option>
                        <Option value="mail">PHP Mail</Option>
                        <Option value="sendmail">Sendmail</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Host"
                      name="host"
                      rules={[{ required: true, message: 'Please input SMTP host!' }]}
                    >
                      <Input placeholder="smtp.gmail.com" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Port"
                      name="port"
                      rules={[{ required: true, message: 'Please input SMTP port!' }]}
                    >
                      <InputNumber min={1} max={65535} placeholder="587" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Encryption" name="encryption">
                      <Select placeholder="Select encryption">
                        <Option value="tls">TLS</Option>
                        <Option value="ssl">SSL</Option>
                        <Option value="">None</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Username"
                      name="username"
                      rules={[{ required: true, message: 'Please input SMTP username!' }]}
                    >
                      <Input placeholder="your-email@gmail.com" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Password"
                      name="password"
                      rules={[{ required: true, message: 'Please input SMTP password!' }]}
                    >
                      <Input.Password placeholder="Enter SMTP password" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="From Address"
                      name="fromAddress"
                      rules={[
                        {
                          required: true,
                          type: 'email',
                          message: 'Please input valid from address!',
                        },
                      ]}
                    >
                      <Input placeholder="noreply@example.com" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="From Name"
                      name="fromName"
                      rules={[{ required: true, message: 'Please input from name!' }]}
                    >
                      <Input placeholder="Kankali Mart" />
                    </Form.Item>
                  </Col>
                </Row>
                <Divider>Email Queue</Divider>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Enable Queue" name="enableQueue" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Enable Retry" name="enableRetry" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>
                <Space style={{ marginTop: 24 }}>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={() => handleSave('email')}
                    loading={loading}
                  >
                    Save Email Settings
                  </Button>
                  <Button icon={<ReloadOutlined />} onClick={() => handleReset('email')}>
                    Reset to Defaults
                  </Button>
                </Space>
              </Form>
            </Tabs.TabPane>

            {/* Notification Settings */}
            <Tabs.TabPane tab="Notifications" key="notifications">
              <Form
                form={notificationForm}
                layout="vertical"
                initialValues={{
                  enableEmailNotifications: true,
                  enablePushNotifications: true,
                  enableSmsNotifications: false,
                  enableInAppNotifications: true,
                  enableOrderNotifications: true,
                  enablePaymentNotifications: true,
                  enableDeliveryNotifications: true,
                  enableSystemNotifications: true,
                  notificationFrequency: 'immediate',
                  quietHours: false,
                  quietStartTime: '22:00',
                  quietEndTime: '08:00',
                }}
              >
                <Divider>Notification Channels</Divider>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Email Notifications"
                      name="enableEmailNotifications"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Push Notifications"
                      name="enablePushNotifications"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="SMS Notifications"
                      name="enableSmsNotifications"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="In-App Notifications"
                      name="enableInAppNotifications"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>
                <Divider>Notification Types</Divider>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Order Notifications"
                      name="enableOrderNotifications"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Payment Notifications"
                      name="enablePaymentNotifications"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Delivery Notifications"
                      name="enableDeliveryNotifications"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="System Notifications"
                      name="enableSystemNotifications"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>
                <Divider>Notification Schedule</Divider>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Notification Frequency" name="notificationFrequency">
                      <Select placeholder="Select frequency">
                        <Option value="immediate">Immediate</Option>
                        <Option value="hourly">Hourly</Option>
                        <Option value="daily">Daily</Option>
                        <Option value="weekly">Weekly</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Quiet Hours" name="quietHours" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>
                <Space style={{ marginTop: 24 }}>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={() => handleSave('notifications')}
                    loading={loading}
                  >
                    Save Notification Settings
                  </Button>
                  <Button icon={<ReloadOutlined />} onClick={() => handleReset('notifications')}>
                    Reset to Defaults
                  </Button>
                </Space>
              </Form>
            </Tabs.TabPane>

            {/* Billing Settings */}
            <Tabs.TabPane tab="Billing" key="billing">
              <Form
                form={billingForm}
                layout="vertical"
                initialValues={{
                  auto_generate_invoice: false,
                }}
              >
                <Divider>Invoice Generation</Divider>
                <Form.Item
                  label="Auto-generate invoice on delivery/completion"
                  name="auto_generate_invoice"
                  valuePropName="checked"
                  tooltip="When ON, an invoice is created automatically when an order becomes delivered/completed. When OFF (recommended), invoices are created manually — combine products + services for the same customer via Combined Bill."
                >
                  <Switch
                    checkedChildren="On"
                    unCheckedChildren="Off"
                    loading={billingLoading}
                  />
                </Form.Item>
                <Alert
                  message="Auto-generation is OFF by default"
                  description="With this switch OFF, delivery/completion no longer auto-creates an invoice. Commissions and loyalty points are still awarded automatically. Use Combined Bill (or the per-order 'Generate Bill' buttons) to create invoices manually."
                  type="info"
                  showIcon
                  style={{ marginTop: 16 }}
                />
                <Space style={{ marginTop: 24 }}>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSaveBilling}
                    loading={updateBillingSettingsMutation.isLoading}
                  >
                    Save Billing Settings
                  </Button>
                </Space>
              </Form>
            </Tabs.TabPane>
          </Tabs>
        </Card>
      )}

      {topTab === 'developer' && (
        <>
          <Alert
            type="warning"
            showIcon
            message="Developer settings"
            description="Technical configuration and diagnostics. Changes here can affect how the platform runs — proceed with care."
            style={{ marginBottom: 16 }}
          />
          <Card
            title={
              <div className="settings-head">
                <span className="settings-head-title">
                  <CodeOutlined /> Developer Mode
                </span>
                <span className="settings-head-sub">
                  Technical configuration, diagnostics, and system health
                </span>
              </div>
            }
            extra={
              <Space>
                <Tooltip title="Clear System Cache">
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={handleClearCache}
                    loading={clearCacheMutation.isLoading}
                  >
                    Clear Cache
                  </Button>
                </Tooltip>
                <Tooltip title="Create Full Backup">
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={() => handleBackup('full')}
                    loading={backupSystemMutation.isLoading}
                  >
                    Backup
                  </Button>
                </Tooltip>
              </Space>
            }
          >
            <Tabs defaultActiveKey="health">
              {/* System Health */}
              <Tabs.TabPane tab="System Health" key="health">
                <SystemHealth embedded />
              </Tabs.TabPane>

              {/* System Settings */}
              <Tabs.TabPane tab="System" key="system">
                <Form
                  form={systemForm}
                  layout="vertical"
                  initialValues={{
                    enableDebugMode: false,
                    enableLogging: true,
                    logLevel: 'info',
                    maxFileSize: 10,
                    enableFileUpload: true,
                    enableImageUpload: true,
                    enableVideoUpload: false,
                    enableApiRateLimit: true,
                    apiRateLimit: 1000,
                    enableCaching: true,
                    cacheTtl: 3600,
                  }}
                >
                  <Divider>System Configuration</Divider>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item label="Debug Mode" name="enableDebugMode" valuePropName="checked">
                        <Switch />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Enable Logging"
                        name="enableLogging"
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item label="Log Level" name="logLevel">
                        <Select placeholder="Select log level">
                          <Option value="debug">Debug</Option>
                          <Option value="info">Info</Option>
                          <Option value="warning">Warning</Option>
                          <Option value="error">Error</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="Max File Size (MB)" name="maxFileSize">
                        <InputNumber min={1} max={100} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Divider>File Upload</Divider>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item
                        label="Enable File Upload"
                        name="enableFileUpload"
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label="Enable Image Upload"
                        name="enableImageUpload"
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label="Enable Video Upload"
                        name="enableVideoUpload"
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Divider>API Configuration</Divider>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Enable API Rate Limit"
                        name="enableApiRateLimit"
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="API Rate Limit (requests/hour)" name="apiRateLimit">
                        <InputNumber min={100} max={10000} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Enable Caching"
                        name="enableCaching"
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="Cache TTL (seconds)" name="cacheTtl">
                        <InputNumber min={60} max={86400} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Space style={{ marginTop: 24 }}>
                    <Button
                      type="primary"
                      icon={<SaveOutlined />}
                      onClick={() => handleSave('system')}
                      loading={loading}
                    >
                      Save System Settings
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={() => handleReset('system')}>
                      Reset to Defaults
                    </Button>
                  </Space>
                </Form>
              </Tabs.TabPane>

              {/* Backup & Google Drive */}
              <Tabs.TabPane tab="Backup" key="backup">
                {backupLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Spin size="large" />
                    <p style={{ marginTop: 16, color: '#999' }}>Loading backup status…</p>
                  </div>
                ) : (
                  <>
                    {/* Backup Status Cards */}
                    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                      <Col xs={24} sm={12} md={6}>
                        <Card size="small">
                          <Statistic
                            title="Recent Backups"
                            value={backupStatus?.total_backups || 0}
                            suffix="files"
                            prefix={<DatabaseOutlined />}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Card size="small">
                          <Statistic
                            title="rclone"
                            value={backupStatus?.rclone?.installed ? 'Installed' : 'Missing'}
                            valueStyle={{
                              color: backupStatus?.rclone?.installed ? '#52c41a' : '#ff4d4f',
                            }}
                            prefix={<CloudUploadOutlined />}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Card size="small">
                          <Statistic
                            title="Drive Connected"
                            value={backupStatus?.rclone?.configured ? 'Yes' : 'No'}
                            valueStyle={{
                              color: backupStatus?.rclone?.configured ? '#52c41a' : '#faad14',
                            }}
                            prefix={<CheckCircleOutlined />}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Card size="small">
                          <Statistic
                            title="Scheduler"
                            value={backupStatus?.scheduler?.container_running ? 'Running' : 'Off'}
                            valueStyle={{
                              color: backupStatus?.scheduler?.container_running
                                ? '#52c41a'
                                : '#ff4d4f',
                            }}
                            prefix={<ClockCircleOutlined />}
                          />
                        </Card>
                      </Col>
                    </Row>

                    {/* Backup Actions */}
                    <Card title="Backup Actions" style={{ marginBottom: 24 }}>
                      <Space direction="vertical" style={{ width: '100%' }} size="large">
                        <Alert
                          message="Automatic Backup Schedule"
                          description={
                            <>
                              The Laravel scheduler runs every minute and automatically triggers a
                              database backup at <strong>3:00 AM daily</strong>. Backups are saved
                              locally and uploaded to Google Drive if rclone is configured.
                            </>
                          }
                          type="info"
                          showIcon
                        />

                        <Row gutter={16}>
                          <Col span={12}>
                            <Button
                              type="primary"
                              icon={<DatabaseOutlined />}
                              size="large"
                              block
                              onClick={() => runDriveBackupMutation.mutate(false)}
                              loading={runDriveBackupMutation.isLoading}
                            >
                              Backup Now (Local Only)
                            </Button>
                          </Col>
                          <Col span={12}>
                            <Button
                              type="primary"
                              icon={<CloudUploadOutlined />}
                              size="large"
                              block
                              danger={!backupStatus?.rclone?.configured}
                              onClick={() => {
                                if (!backupStatus?.rclone?.configured) {
                                  Modal.confirm({
                                    title: 'rclone not configured',
                                    content:
                                      'rclone is not configured for Google Drive. The backup will be saved locally only. Continue?',
                                    okText: 'Backup Locally',
                                    cancelText: 'Cancel',
                                    onOk: () => runDriveBackupMutation.mutate(false),
                                  });
                                } else {
                                  runDriveBackupMutation.mutate(true);
                                }
                              }}
                              loading={runDriveBackupMutation.isLoading}
                            >
                              Backup & Upload to Drive
                            </Button>
                          </Col>
                        </Row>

                        <Button
                          icon={<ReloadOutlined />}
                          onClick={() => refetchBackup()}
                          loading={backupLoading}
                        >
                          Refresh Status
                        </Button>
                      </Space>
                    </Card>

                    {/* Recent Backups List */}
                    <Card title="Recent Backups" style={{ marginBottom: 24 }}>
                      {backupStatus?.backups?.length > 0 ? (
                        <List
                          size="small"
                          dataSource={backupStatus.backups}
                          renderItem={(backup) => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={
                                  <DatabaseOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                                }
                                title={<code>{backup.filename}</code>}
                                description={`${backup.size} — ${backup.created_at}`}
                              />
                              <Tag color="blue">{backup.size}</Tag>
                            </List.Item>
                          )}
                        />
                      ) : (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                          <DatabaseOutlined style={{ fontSize: 32, marginBottom: 8 }} />
                          <p>No backups found. Run your first backup above.</p>
                        </div>
                      )}
                    </Card>

                    {/* Setup Instructions */}
                    <Card title="Google Drive Setup">
                      <Typography.Paragraph>
                        To enable automatic uploads to Google Drive, you need to install and
                        configure <Typography.Text code>rclone</Typography.Text> on your server.
                      </Typography.Paragraph>
                      <Typography.Paragraph>
                        <ol>
                          <li>
                            Install rclone:
                            <Typography.Paragraph>
                              <code>brew install rclone</code> (macOS) or{' '}
                              <code>sudo apt install rclone</code> (Linux)
                            </Typography.Paragraph>
                          </li>
                          <li>
                            Configure Google Drive remote:
                            <Typography.Paragraph>
                              <code>rclone config</code> → choose &quot;n&quot; (new remote) → name
                              it &quot;gdrive&quot; → select &quot;drive&quot; → follow the OAuth
                              flow
                            </Typography.Paragraph>
                          </li>
                          <li>
                            Create the backup folder:
                            <Typography.Paragraph>
                              <code>rclone mkdir gdrive:kankali-mart-backups</code>
                            </Typography.Paragraph>
                          </li>
                          <li>
                            Test the connection:
                            <Typography.Paragraph>
                              <code>rclone ls gdrive:kankali-mart-backups</code>
                            </Typography.Paragraph>
                          </li>
                          <li>
                            Or use the manual script:
                            <Typography.Paragraph>
                              <code>./scripts/db-backup.sh</code>
                            </Typography.Paragraph>
                          </li>
                        </ol>
                      </Typography.Paragraph>
                      <Alert
                        message="Need help?"
                        description="Run these commands on your Docker host machine (not inside a container). rclone needs interactive OAuth setup."
                        type="info"
                        showIcon
                      />
                    </Card>
                  </>
                )}
              </Tabs.TabPane>
            </Tabs>
          </Card>
        </>
      )}
    </div>
  );
};

export default Settings;
